<template>
  <div v-if="!error">
    <Slider :show-slides="$props.showSlides" @slide-change="onSlide">
      <template v-for="(entry, index) in entries" :key="index">
        <Team :id="`list-entry-` + index" :person="entry" />
      </template>
      <div v-for="index in entryCount - entries.length" :key="index">
        <div
          class="flex items-center justify-center w-full h-full bg-gray-300 animate-pulse"
        >
          <FaIcon icon-class="fas fa-circle-notch fa-spin mx-2xs" />
        </div>
      </div>
    </Slider>
  </div>
  <div v-else><LoadingError /></div>
</template>

<script setup lang="ts">
import Slider from '@/components/slider/slider.vue';
import { useCachedPost } from '~~/src/composables/dataFetching/genericFetchers.js';
import Team from './team.vue';
import type { TeamData } from '../../blockType';
import { ESectionHandle } from '~~/src/@types/section-handle';
import FaIcon from '@/components/fa-icon.vue';
import { LoadingError } from '@/complib';
import { handleLoadingError } from '~/utils/handleLoadingError';

const props = defineProps({
  blockData: {
    type: Object as PropType<TeamData>,
    required: true,
    default: {} as TeamData,
  },
  showSlides: {
    type: Number,
    required: false,
    default: 4,
  },
});

const error = ref(false);

type ListArgs = {
  id: string[];
  sectionId: string[];
};

const listArgs: ListArgs = {
  id: props.blockData.ids,
  sectionId: [ESectionHandle.PERSON],
};

const entries = ref(props.blockData.initalPersons);
const entryCount = ref(props.blockData.ids.length);
const isReloading = ref(false);

interface loadEntriesParams extends ListArgs {
  limit: number;
  offset: number;
}
const site = useSiteIdent();

async function loadEntries(queryArgs: loadEntriesParams) {
  try {
    const { data: fetchData } = await useCachedPost(
      `/api/${site}/content/list/extendable`,
      queryArgs,
    );

    if (entries.value.length) {
      entries.value.push(...fetchData.value);
    } else {
      entries.value = fetchData.value;
    }
  } catch (e) {
    error.value = true;
    handleLoadingError(e);
  }
}

async function loadMoreEntries(index: number) {
  if (isReloading.value) {
    return;
  }
  isReloading.value = true;
  if (entries.value.length < entryCount.value) {
    await loadEntries({
      id: listArgs.id,
      sectionId: listArgs.sectionId,
      limit: Math.max(props.showSlides * 4, index + props.showSlides * 2),
      offset: entries.value.length,
    });
  }
  isReloading.value = false;
}

const onSlide = async (nextSlide: number) => {
  if (nextSlide > entries.value.length - props.showSlides) {
    await loadMoreEntries(nextSlide);
  }
};
</script>
